import { Body, SectionHeader, SecondaryBody, Link } from "@sprout/typography";
import { PrimaryButton } from "@sprout/buttons";
import { usePage } from "@inertiajs/react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./application_form.scss";
import type { FieldValue } from "./field";
import { Field } from "./field";
import { DemographicSection } from "./demographic_section";
import { EeocForm } from "./eeoc/form";
import UploadManager from "../../utils/uploads/upload_manager";
import { useTranslation } from "react-i18next";
import {
  buildApplication,
  type DemographicAnswer,
  type EmploymentAnswers,
  isBasicField,
} from "@utils/jben/build_application.ts";
import { isUploadedFile } from "@utils/uploads/uploaded_file.ts";
import {
  type JobPost,
  type Question,
  type ComplianceAnswers,
  type Field as FieldType,
  type SearchSpecificFields,
} from "../../types/jben/job_post";
import {
  validateApplication,
  type EducationErrors,
  type ErrorWithLink,
} from "@utils/jben/validate_application.ts";
import { isDemographicDataConsentRequired } from "@utils/jben/demographic_data_consent";
import { submitApplication } from "@utils/jben/submit_application";
import { ApplicationDescription } from "./application_description";
import { ComplianceSection } from "./compliance_section";
import { scrollToElement } from "@utils/jben/scroll_to_element";
import { EmploymentSection } from "./employment_section";
import { employmentRequired, showEmployment } from "@utils/jben/job_helpers";
import { type EducationAnswer, EducationSection } from "./education_section";
import EmailField from "./email_field";
import { useShowFlash } from "@sprout/flash.tsx";
import PhoneField, { type PhoneError } from "./phone_field";
import { LocationInput, type Location } from "@components/ui/location_input";
import { appendSearchParams } from "@utils/jben/append_search_params";
import { toSnakeCase } from "@utils/jben/string_helpers";
import { BoardConfigurationContext } from "@contexts/board_configuration_context";
import type { BoardConfiguration } from "@/types/jben/board_configuration.ts";
import DOMPurify from "dompurify";
import ApplicationFormHeader from "./application_form_header";
import useWhoami from "@/queries/use_whoami";
import { Autofiller } from "@components/job_post/autofill/autofiller";
import { EducationAutofiller } from "@components/job_post/autofill/education_autofiller";
import { EmploymentAutofiller } from "@components/job_post/autofill/employment_autofiller";
import ApplicationDreamJobBanner from "@components/job_post/application_dream_job_banner.tsx";

interface Props {
  jobPost: JobPost & SearchSpecificFields;
  questions: Question[];
  submitPath: string;
  boardConfiguration: BoardConfiguration;
  onSuccess?: (confirmationPath: string) => void;
  showBasicFields?: boolean;
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
}

export type FormField = FieldType & {
  label: string;
  description?: string | null;
  required: boolean;
  multiSelectStyle?: string;
  allowManual: boolean;
  error?: string;
  allowedFiletypes?: string[];
  custom: boolean;
};

function displayEducation(jobPost: JobPost) {
  if (!jobPost || !jobPost.education_config) return false;
  for (const value of Object.values(jobPost.education_config)) {
    if (value === "optional" || value === "required") return true;
  }
  return false;
}

function classifyFileAnswers(answers: Record<string, FieldValue>) {
  const now = Date.now();
  const expired: string[] = [];
  const upcoming: Array<{ key: string; msUntilExpiry: number }> = [];
  for (const [key, value] of Object.entries(answers)) {
    if (!isUploadedFile(value)) continue;
    const msUntilExpiry = value.expiresAt - now;
    if (msUntilExpiry <= 0) expired.push(key);
    else upcoming.push({ key, msUntilExpiry });
  }
  return { expired, upcoming };
}

export const ApplicationForm = ({
  jobPost,
  questions,
  submitPath,
  boardConfiguration,
  onSuccess,
  showBasicFields = true,
  educationApiUrl,
  dreamJobAvailable = false,
}: Props) => {
  const { t } = useTranslation("job_post");

  const [answers, setAnswers] = useState<Record<string, FieldValue>>({});
  const [phoneError, setPhoneError] = useState<PhoneError | null>(null);
  const [demographicAnswers, setDemographicAnswers] = useState<Record<string, DemographicAnswer[]>>(
    {},
  );
  const [complianceAnswers, setComplianceAnswers] = useState<ComplianceAnswers>({});
  const [employmentAnswers, setEmploymentAnswers] = useState<EmploymentAnswers>([{ key: 0 }]);
  const [educationAnswers, setEducationAnswers] = useState<EducationAnswer[]>([{ key: 0 }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [demographicErrors, setDemographicErrors] = useState<Record<string, string>>({});
  const [complianceErrors, setComplianceErrors] = useState<Record<string, string | ErrorWithLink>>(
    {},
  );
  const [employmentErrors, setEmploymentErrors] = useState<Record<string, string>[]>([]);
  const [educationErrors, setEducationErrors] = useState<Record<number, EducationErrors>>({});
  const [eeocErrors, setEeocErrors] = useState<Record<string, string>>({});
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [prefillComplete, setPrefillComplete] = useState(false);
  const [markAsDreamJob, setMarkAsDreamJob] = useState(false);

  const s3Manager = useRef(new UploadManager());
  const hasPrefilled = useRef(false);

  const { data: whoamiProfile } = useWhoami();
  const { currentUserEmail } = usePage<{ currentUserEmail?: string }>().props;

  const displayEducationForm = useMemo(() => displayEducation(jobPost), [jobPost]);

  const demographicDataConsentRequired = useMemo(() => {
    return isDemographicDataConsentRequired(jobPost, Object.values(demographicAnswers));
  }, [demographicAnswers, jobPost]);

  useEffect(() => {
    s3Manager.current.initUploaders(s3Manager.current.inputFilesFrom(questions));
  }, [questions]);

  useEffect(() => {
    const expiredMessage = t("file_upload.expired") || "";
    const { expired, upcoming } = classifyFileAnswers(answers);
    const expiredSet = new Set(expired);

    const handles = upcoming.map(({ key, msUntilExpiry }) =>
      setTimeout(() => {
        setErrors((current) => ({ ...current, [key]: expiredMessage }));
      }, msUntilExpiry),
    );

    setErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of expired) {
        if (next[key] !== expiredMessage) {
          next[key] = expiredMessage;
          changed = true;
        }
      }
      for (const key of Object.keys(next)) {
        if (next[key] !== expiredMessage || expiredSet.has(key)) continue;
        delete next[key];
        changed = true;
      }
      return changed ? next : prev;
    });

    return () => handles.forEach((h) => clearTimeout(h));
  }, [answers, t]);

  useEffect(() => {
    const { data_compliance, demographic_questions } = jobPost;
    if (!data_compliance) return;

    const initialComplianceAnswers: ComplianceAnswers = {};

    if (data_compliance.single_purpose_consent) {
      if (data_compliance.requires_processing_consent) {
        initialComplianceAnswers["gdpr_processing_consent_given"] = false;
      }
      if (data_compliance.requires_retention_consent) {
        initialComplianceAnswers["gdpr_retention_consent_given"] = false;
      }
    } else if (data_compliance.retention_period) {
      initialComplianceAnswers["gdpr_consent_given"] = false;
    }

    if (
      data_compliance.single_purpose_consent &&
      data_compliance.demographic_data_consent_applies &&
      demographic_questions
    ) {
      initialComplianceAnswers["gdpr_demographic_data_consent_given"] = false;
    }

    setComplianceAnswers(initialComplianceAnswers);
  }, [jobPost]);

  useEffect(() => {
    const elementWithError = document.querySelector<HTMLElement>(
      '[aria-invalid="true"], [data-error="true"], .helper-text--error',
    );
    if (!elementWithError) return;
    scrollToElement(elementWithError);
    elementWithError.focus();
  }, [errors, demographicErrors, complianceErrors, employmentErrors, educationErrors, eeocErrors]);

  useEffect(() => {
    if (!whoamiProfile || hasPrefilled.current) return;
    hasPrefilled.current = true;

    const eeocQuestions = jobPost.eeoc_sections?.flatMap((s) => s.questions) ?? [];

    const autofiller = new Autofiller(
      answers,
      setAnswers,
      questions,
      eeocQuestions,
      new EducationAutofiller(
        educationAnswers,
        setEducationAnswers,
        jobPost.education_config,
        educationApiUrl,
      ),
      new EmploymentAutofiller(employmentAnswers, setEmploymentAnswers, jobPost.employment),
    );

    void autofiller.fill(whoamiProfile).then(() => setPrefillComplete(true));
  }, [whoamiProfile, questions, jobPost]);

  const showFlash = useShowFlash();

  const handleSubmitErrorMessage = useCallback(
    (message: string, error?: Error) => {
      if (error) console.error(error);
      setSubmitErrorMessage(message);
      showFlash(message, "error");
      setSubmitting(false);
    },
    [showFlash],
  );

  const fields = useMemo(
    () =>
      questions.map((question): FormField => {
        const field = question.fields[0];
        const isFile = field.type === "input_file";
        const allowManual = isFile && question.fields.length > 1;

        return {
          ...field,
          label: question.label,
          description: question.description,
          required: question.required,
          multiSelectStyle: question.multi_select_style,
          allowManual,
          error: errors[field.name],
          allowedFiletypes: field.allowed_filetypes,
          custom: field.name.startsWith("question_"),
        };
      }),
    [questions, errors],
  );

  const trackingParams = new URLSearchParams(window.location.search);

  const prepareRequest = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);

    const application = buildApplication(
      jobPost,
      { ...answers, email: currentUserEmail ?? "" },
      demographicAnswers,
      complianceAnswers,
      employmentAnswers,
      educationAnswers,
      trackingParams,
    );

    const validationResult = validateApplication(jobPost, application, phoneError, showBasicFields);
    const { expired } = classifyFileAnswers(answers);
    const expiredMessage = t("file_upload.expired") || "";
    const expiredFileErrors = Object.fromEntries(expired.map((key) => [key, expiredMessage]));

    if (!validationResult.isValid || expired.length > 0) {
      setSubmitting(false);
      setErrors({ ...validationResult.basicErrors, ...expiredFileErrors });
      setDemographicErrors(validationResult.demographicErrors);
      setComplianceErrors(validationResult.complianceErrors);
      setEmploymentErrors(validationResult.employmentErrors);
      setEducationErrors(validationResult.educationErrors);
      setEeocErrors(validationResult.eeocErrors);
      return;
    }

    return application;
  };

  const handleErrors = async (responseData: unknown) => {
    const body = responseData as { code: string; message: string };

    if (body.code === "invalid-attributes") {
      const attributes = body.message.split(",");
      const eeocFields =
        jobPost.eeoc_sections?.flatMap(
          (section) =>
            section?.questions?.map((question) => ({
              name: question.fields[0].name,
              label: question.label,
            })) || [],
        ) ?? [];
      const complianceFields = [
        {
          name: jobPost.data_compliance?.single_purpose_consent
            ? "gdpr_processing_consent_given"
            : "gdpr_consent_given",
          label: "",
        },
      ];
      const allFields = [...fields, ...eeocFields, ...complianceFields];
      const invalidErrors: { [key: string]: string } = {};

      for (const attribute of attributes) {
        const field = allFields.find((f) => f.name === attribute);
        if (!field) continue;
        if (attribute.includes("gdpr")) {
          invalidErrors[attribute] = t("application.accept_terms");
          continue;
        }
        invalidErrors[attribute] = t("application.errors.invalid_attribute", {
          attribute: field.label.toLowerCase(),
        });
      }

      setErrors(invalidErrors);
      setEeocErrors(invalidErrors);
      setComplianceErrors(invalidErrors);

      if (Object.keys(invalidErrors).length === 0) {
        handleSubmitErrorMessage(t("application.errors.generic_failure"));
      }
    } else if (body.code === "invalid-attachment") {
      const invalidErrors: { [key: string]: string } = {};
      const fieldNames = fields.map((field) => field.name);
      const errorMessages = body.message.split(".").filter((message: string) => !!message);

      errorMessages.forEach((message: string) => {
        const match = message.trim().match(/^Uploaded (.*?) has an unsupported file type$/);
        if (match && match[1]) {
          const erroredFieldName = toSnakeCase(match[1]);
          if (fieldNames.includes(erroredFieldName)) {
            invalidErrors[erroredFieldName] = t("application.errors.invalid_filetype");
          }
        }
      });
      setErrors(invalidErrors);

      const invalidErrorsLength = Object.keys(invalidErrors).length;
      if (invalidErrorsLength === 0 || invalidErrorsLength !== errorMessages.length) {
        handleSubmitErrorMessage(t("application.errors.generic_invalid_filetype"));
      }
    } else if (body.code === "not-found" || body.code === "not-live" || body.code === "stale") {
      handleSubmitErrorMessage(t("application.errors.generic_failure"));
    } else {
      handleSubmitErrorMessage(t("application.errors.generic_failure"));
    }

    setSubmitting(false);
  };

  const handleSubmit = async (event: React.SyntheticEvent) => {
    const application = prepareRequest(event);
    if (!application) return;

    try {
      const response = await submitApplication({
        application,
        submitPath,
        fingerprint: jobPost.fingerprint,
        markAsDreamJob,
      });

      if (response.status >= 200 && response.status < 300) {
        const confirmationPath = response.data?.goto || "/";
        if (onSuccess) {
          onSuccess(appendSearchParams(confirmationPath, trackingParams));
        } else {
          window.location.assign(appendSearchParams(confirmationPath, trackingParams));
        }
      } else {
        await handleErrors(response.data);
      }
    } catch (e) {
      const err = e as { response?: { data: unknown } };
      if (err.response) {
        await handleErrors(err.response.data);
      } else {
        handleSubmitErrorMessage(
          t("application.errors.generic_failure"),
          e instanceof Error ? e : undefined,
        );
      }
    }
  };

  const handleChange = (key: string, value: FieldValue) => {
    setAnswers({ ...answers, [key]: value });
  };

  const handleFileInputChange = (key: string, value: FieldValue) => {
    const textKey = `${key}_text`;
    let updatedAnswers: { [key: string]: string | FieldValue | null };

    if (typeof value === "string") {
      updatedAnswers = { [key]: null, [textKey]: value };
    } else {
      updatedAnswers = { [key]: value, [textKey]: null };
    }

    setAnswers((previousAnswers) => ({ ...previousAnswers, ...updatedAnswers }));
  };

  const handleCandidateLocationChange = (location: Location | null) => {
    if (!location) {
      return setAnswers({
        ...answers,
        location: null,
        latitude: null,
        longitude: null,
        country_short_name: null,
      });
    }

    setAnswers({
      ...answers,
      location: location.display_name,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      country_short_name: location.country_short_name ?? null,
    });
  };

  const candidateLocation: Location | null = useMemo(() => {
    if (!answers.location) return null;
    return {
      display_name: answers.location as string,
      latitude: parseFloat(answers.latitude as string) || 0,
      longitude: parseFloat(answers.longitude as string) || 0,
      country_short_name: (answers.country_short_name as string) || null,
    };
  }, [answers.location, answers.latitude, answers.longitude, answers.country_short_name]);

  const handleDemographicChange = (key: string, value: DemographicAnswer[]) => {
    setDemographicAnswers({ ...demographicAnswers, [key]: value });
  };

  const handleComplianceChange = (key: keyof ComplianceAnswers, value: boolean) => {
    setComplianceAnswers({ ...complianceAnswers, [key]: value });

    if (
      "gdpr_retention_consent_given" in complianceAnswers &&
      key === "gdpr_processing_consent_given" &&
      !value
    ) {
      setComplianceAnswers((prev) => ({
        ...prev,
        gdpr_retention_consent_given: false,
      }));
    }
  };

  const hasCustomFields = fields.some((f) => f.custom);

  const renderStandardFields = () => renderFields(false);
  const renderCustomFields = () => renderFields(true);

  const renderLocationField = () => (
    <div className="field-wrapper" data-provides="candidate-location">
      <LocationInput value={candidateLocation} onChange={handleCandidateLocationChange} />
      {errors.candidate_location && (
        <p
          id="candidate_location-error"
          className="helper-text helper-text--error"
          aria-live="polite"
        >
          {errors.candidate_location}
        </p>
      )}
    </div>
  );

  const renderFields = (renderCustom = false) => {
    return (
      <div className="application--questions">
        {renderCustom && showBasicFields && <hr />}
        {fields
          .filter((f) => f.custom === renderCustom)
          .filter((f) => showBasicFields || !isBasicField(f.name))
          .map((field) => {
            if (field.name === "phone") {
              return (
                <Fragment key={field.name}>
                  <div>
                    <PhoneField
                      key={prefillComplete ? "prefilled" : "initial"}
                      label={field.label}
                      name={field.name}
                      required={field.required}
                      countryError={errors.country}
                      phoneError={errors.phone}
                      value={answers[field.name] as string | null}
                      onChange={(value) => handleChange(field.name, value)}
                      onError={setPhoneError}
                    />
                  </div>
                </Fragment>
              );
            }

            if (field.name === "candidate_location") {
              return <Fragment key={field.name}>{renderLocationField()}</Fragment>;
            }

            if (field.name === "email") {
              return (
                <div key={field.name} className="field-wrapper">
                  <EmailField
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    options={field.values}
                    required={field.required}
                    error={field.error}
                    disabled
                    onChange={() => {}}
                    onFocus={() => {}}
                    value={currentUserEmail ?? ""}
                  />
                </div>
              );
            }

            return (
              <div
                key={field.type === "input_file" ? `${field.name}-${prefillComplete}` : field.name}
                className={
                  field.type === "textarea"
                    ? "field-wrapper field-wrapper--multiline"
                    : "field-wrapper"
                }
              >
                <Field
                  label={field.label}
                  name={field.name}
                  type={field.type}
                  options={field.values}
                  required={field.required}
                  multiSelectStyle={field.multiSelectStyle}
                  error={field.error}
                  allowManual={field.allowManual}
                  onChange={(value) => {
                    const key = field.name;
                    if (field.type === "input_file") {
                      handleFileInputChange(key, value);
                    } else {
                      handleChange(key, value);
                    }
                  }}
                  onFocus={() => {}}
                  uploadManager={s3Manager.current}
                  value={answers[field.name]}
                  allowedFiletypes={field.allowedFiletypes}
                />

                {field.description && (
                  <Body as="div" secondary>
                    <div
                      id={`${field.name}-description`}
                      className="question-description"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                    ></div>
                  </Body>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <BoardConfigurationContext.Provider value={boardConfiguration}>
      <ApplicationFormHeader
        title={jobPost.title}
        companyName={jobPost.company_name}
        logoUrl={jobPost.logo_url}
        publicUrl={jobPost.public_url}
        firstPublished={jobPost.first_published}
        locations={jobPost.locations}
        workType={jobPost.work_type}
        payRange={jobPost.pay_range_string ?? undefined}
      />
      <ApplicationDescription
        introduction={jobPost.introduction}
        content={jobPost.content}
        conclusion={jobPost.conclusion}
        payRanges={jobPost.pay_ranges}
      />
      <div className="jben-form jben-form--container">
        <div className="application--header">
          <div className="application--header--text">
            <div className="application--header--title">
              <SectionHeader as="h2">{t("application.apply_to_job")}</SectionHeader>
            </div>
            <div className="application--header--required">
              <div className="application--header--required--asterisk">
                <SecondaryBody>{"*"}</SecondaryBody>
              </div>
              <SecondaryBody>{t("application.required")}</SecondaryBody>
            </div>
          </div>
        </div>
        <form id={"application-form"} onSubmit={handleSubmit} className="application--form">
          {showBasicFields && (
            <>
              {renderStandardFields()}
              {showEmployment(jobPost) && (
                <EmploymentSection
                  employments={employmentAnswers}
                  required={employmentRequired(jobPost)}
                  errors={employmentErrors}
                  onChange={setEmploymentAnswers}
                />
              )}
              {displayEducationForm && (
                <EducationSection
                  educationConfiguration={jobPost.education_config!}
                  educationApiUrl={educationApiUrl}
                  onChange={setEducationAnswers}
                  answers={educationAnswers}
                  errors={educationErrors}
                />
              )}
            </>
          )}

          {hasCustomFields && renderCustomFields()}

          {jobPost.demographic_questions && (
            <>
              <hr />
              <DemographicSection
                title={jobPost.demographic_questions.title}
                description={jobPost.demographic_questions.description}
                questions={jobPost.demographic_questions.questions}
                errors={demographicErrors}
                onChange={handleDemographicChange}
              />
            </>
          )}

          {jobPost.eeoc_sections && (
            <EeocForm
              sections={jobPost.eeoc_sections}
              errors={eeocErrors}
              onChange={handleChange}
              answers={answers}
            />
          )}

          {jobPost.data_compliance && (
            <>
              <hr />
              <ComplianceSection
                internal={false}
                complianceAnswers={complianceAnswers}
                data={jobPost.data_compliance}
                errors={complianceErrors}
                companyName={jobPost.company_name}
                onChange={handleComplianceChange}
                demographicDataConsentRequired={demographicDataConsentRequired}
              />
            </>
          )}

          {jobPost.application_deadline && (
            <p>
              {t("post.application_deadline", {
                date: new Date(jobPost.application_deadline).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
              })}
            </p>
          )}

          {jobPost.include_ai_disclaimer && (
            <>
              <hr />
              <div className="application--ai-disclaimer">
                {jobPost.ai_disclaimer && (
                  <div className="content">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(jobPost.ai_disclaimer),
                      }}
                    />
                    {jobPost.ai_opt_out_request_url && (
                      <Link href={jobPost.ai_opt_out_request_url} external>
                        {t("ai_disclaimer.learn_more")}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {dreamJobAvailable && (
            <ApplicationDreamJobBanner marked={markAsDreamJob} onChange={setMarkAsDreamJob} />
          )}

          {submitErrorMessage && (
            <p id="submit-error" className="helper-text helper-text--error" aria-live="polite">
              {submitErrorMessage}
            </p>
          )}

          <div className="application--submit">
            <PrimaryButton
              type="submit"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
            >
              {t("application.submit")}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </BoardConfigurationContext.Provider>
  );
};
